import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import useToggle from 'hooks/useToggle'
import { searchResultPolicyClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BedbankCancellationPolicyModal from './BedbankCancellationPolicyModal'
import BedbankPropertyCancellationPoliciesBase from 'components/Common/CancellationPolicies/Bedbank/BedbankPropertyCancellationPoliciesBase'

const CancellationPolicies = styled.span`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: ${rem(4)}
  }
`

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  rate: App.BedbankRate | App.OrderBedbankRate;
  showNonCancellation: boolean;
  isConfirmation?: boolean;
  className?: string;
}

function BedbankCancellationPolicy(props: Props) {
  const { rate, isConfirmation, showNonCancellation } = props
  const { on: openPolicy, off: closePolicy, value: isPolicyOpen } = useToggle()

  const handlePolicyOn = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    openPolicy()
    addGTMEvent(searchResultPolicyClick())
  }, [openPolicy])

  const handlePolicyOff = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    closePolicy()
  }, [closePolicy])

  const modalHeading = useMemo(() => {
    if (rate.partiallyRefundable) {
      return 'Partially refundable'
    }
    return rate.refundable ? 'Free cancellation' : 'Non-refundable and no date changes permitted'
  }, [rate.partiallyRefundable, rate.refundable])

  return <>
    {rate.partiallyRefundable || (rate.refundable && !isConfirmation) || (!rate.partiallyRefundable && !rate.refundable && showNonCancellation) &&
      <Clickable onClick={handlePolicyOn}>
        {rate.partiallyRefundable &&
          <CancellationPolicies>
            <BodyText variant="medium" as="span">Partially refundable</BodyText>
            <SolidInfoCircleIcon size="XS" />
          </CancellationPolicies>
        }
        {rate.refundable && !isConfirmation &&
          <BedbankPropertyCancellationPoliciesBase rate={rate} />
        }
        {!rate.partiallyRefundable && !rate.refundable && showNonCancellation &&
          <CancellationPolicies>
            <BodyText variant="medium" as="span">Non-refundable</BodyText>
            <SolidInfoCircleIcon size="XS" />
          </CancellationPolicies>
      }
      </Clickable>
    }
    {(rate.partiallyRefundable || (rate.cancellationPolicy && (showNonCancellation || rate.refundable))) && (
      <BedbankCancellationPolicyModal heading={modalHeading} isOpen={isPolicyOpen} onClose={handlePolicyOff} policyDescriptions={rate.cancellationPolicy.description ?? []} />
    )}
  </>
}

export default BedbankCancellationPolicy
